import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Plus } from '@factorialco/f0-react/icons/app'
import {
  CancelSchedulePostsPostDocument,
  DeletePostDocument,
  GetCommunitiesDocument,
  GetPostsDocument,
  GetPostsQuery,
  UpdatePostDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import ManagePost from 'modules/posts/components/ManagePost'

type PostsPost = NonNullable<GetPostsQuery['posts']['postsConnection']['nodes'][0]>

const ConfirmDeleteModalOpen = ({
  onClose,
  onSubmit,
}: {
  onClose: () => void
  onSubmit: () => void
}) => {
  return (
    <Modal
      illustration="alert"
      title={i18n.t('posts.communities.modals.delete_post.title')}
      description={i18n.t('posts.communities.modals.delete_post.description')}
      onClose={onClose}
      disableFocusTrap
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('posts.common.cancel')}
          </Button>
          <Button type="primary" onClick={onSubmit}>
            {i18n.t('posts.common.proceed')}
          </Button>
        </>
      }
    />
  )
}

const CreatePostModal = ({ onClose, onSubmit }: { onClose: () => void; onSubmit: () => void }) => {
  return (
    <Modal
      title={i18n.t('posts.communities.modals.create_post.title')}
      onClose={onClose}
      disableFocusTrap
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('posts.common.cancel')}
          </Button>
          <Button type="primary" onClick={onSubmit}>
            {i18n.t('posts.common.proceed')}
          </Button>
        </>
      }
    >
      <ManagePost isEvent={false} />
    </Modal>
  )
}

const PostsListPage = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { access } = useCurrentSession()
  const [toDeletePostId, setToDeletePostId] = useState<number | null>(null)
  const [createPostModalOpen, setCreatePostModalOpen] = useState(false)
  const { data: postGroups, loading: isLoadingPostGroups } = useQuery(GetCommunitiesDocument)

  const source = useGraphqlDataCollectionSource(
    {
      document: GetPostsDocument,
      mapVariables: ({ filters: _filters, search: _search }) => ({
        status: _filters.status ?? [],
        groupIds: _filters.communities,
        search: _search ?? '',
        authorIds: [access.id],
      }),
      mapPagination: (data: GetPostsQuery) => ({
        total: data?.posts?.postsConnection?.totalCount ?? 0,
        cursor: data?.posts?.postsConnection?.pageInfo?.endCursor ?? undefined,
        hasNextPage: data?.posts?.postsConnection?.pageInfo?.hasNextPage ?? false,
      }),
      search: {
        enabled: true,
      },
      mapData: (data: GetPostsQuery) => {
        return data?.posts?.postsConnection?.nodes ?? []
      },
      filters: {
        status: {
          label: i18n.t('posts.communities.filters.status'),
          type: 'in',
          options: {
            options: [
              { label: i18n.t('posts.communities.status.scheduled'), value: 'scheduled' },
              { label: i18n.t('posts.communities.status.published'), value: 'published' },
              { label: i18n.t('posts.communities.status.draft'), value: 'draft' },
            ],
          },
        },
        ...(!isLoadingPostGroups
          ? {
              communities: {
                label: i18n.t('posts.communities.filters.community'),
                type: 'in',
                options: {
                  options:
                    postGroups?.posts?.groupsConnection?.nodes?.map((group) => ({
                      label: group.title,
                      value: group.id,
                    })) ?? [],
                },
              },
            }
          : {}),
      },
      presets: [
        {
          label: i18n.t('posts.communities.status.scheduled'),
          filter: { status: ['scheduled'] },
        },
        {
          label: i18n.t('posts.communities.status.published'),
          filter: { status: ['published'] },
        },
        {
          label: i18n.t('posts.communities.status.draft'),
          filter: { status: ['draft'] },
        },
      ],
      defaultSortings: {
        field: 'updatedAt',
        order: 'asc',
      },
      paginationType: 'infinite-scroll',
      itemUrl: (post: PostsPost) => navigation.communities.post.id.fullPath({ id: post.id }),
      itemActions: (post: PostsPost) => [
        ...(post.scheduled || post.draft
          ? [
              {
                label: i18n.t('posts.communities.publish_now'),
                onClick: async () => {
                  const response = await updatePost({
                    variables: createUpdatePostVariables(post, new Date().toISOString()),
                  })

                  const handled = handleResult(response, () => undefined)
                  if (!handled.success) {
                    app.setFlash({
                      type: 'wrong',
                      message: i18n.t('posts.communities.quick_post.error_message'),
                    })
                    return
                  }

                  app.setFlash({
                    type: 'correct',
                    message: i18n.t('posts.communities.quick_post.success_message'),
                  })
                },
                type: 'primary' as const,
              },
            ]
          : []),
        ...(post.scheduled
          ? [
              {
                label: i18n.t('posts.communities.cancel_schedule'),
                onClick: async () => {
                  const response = await cancelPostSchedule({
                    variables: {
                      id: post.id,
                    },
                  })

                  const handled = handleResult(response, () => undefined)
                  if (!handled.success) {
                    app.setFlash({
                      type: 'wrong',
                      message: i18n.t('posts.communities.cancel_schedule_error'),
                    })
                    return
                  }

                  app.setFlash({
                    type: 'correct',
                    message: i18n.t('posts.communities.cancel_schedule_success'),
                  })
                },
                type: 'secondary' as const,
              },
            ]
          : []),
        {
          label: i18n.t('posts.communities.post.delete'),
          onClick: () => setToDeletePostId(post.id),
          type: 'secondary' as const,
        },
      ],
      primaryActions: () => [
        {
          label: i18n.t('new_dashboard.posts.write_post'),
          icon: Plus,
          onClick: handleClickCreatePost,
        },
      ],
    },
    [postGroups]
  )

  const [deletePost] = useMutation(DeletePostDocument, {
    variables: {
      postId: toDeletePostId ?? 0,
    },
    extractorFn: (data) => data.postsMutations.deletePostsPost,
    refetchQueries: [GetPostsDocument],
    awaitRefetchQueries: true,
  })

  const [updatePost] = useMutation(UpdatePostDocument, {
    extractorFn: (data) => data.postsMutations.updatePostsPost,
    refetchQueries: [GetPostsDocument],
    awaitRefetchQueries: true,
  })

  const [cancelPostSchedule] = useMutation(CancelSchedulePostsPostDocument, {
    extractorFn: (data) => data.postsMutations.cancelSchedulePostsPost,
    refetchQueries: [GetPostsDocument],
    awaitRefetchQueries: true,
  })

  const createUpdatePostVariables = (post: PostsPost, publishedAt: string) => {
    return {
      id: post.id,
      title: post.title || '',
      description: post.description || '',
      allowCommentsAndReactions: post.allowCommentsAndReactions ?? true,
      sendNotifications: post.sendNotifications ?? false,
      mentionedAccessIds: post.mentionedAccessIds || [],
      postGroupId: post.group?.id,
      location: post.location || undefined,
      startsAt: post.startsAt || undefined,
      publishedAt,
      deleteCoverImage: false,
      deletedAttachments: [],
      newAttachments: undefined,
      newCoverImage: undefined,
    }
  }

  const handleSubmit = async () => {
    if (!toDeletePostId) return
    const response = await deletePost({
      variables: {
        postId: toDeletePostId ?? 0,
      },
    })

    const handled = handleResult(response, () => undefined)
    if (!handled.success)
      return app.setFlash({
        type: 'wrong',
        message: i18n.t('posts.communities.error_deleting_post'),
      })

    app.setFlash({
      type: 'correct',
      message: i18n.t('posts.communities.post_deleted_successfully'),
    })
    setToDeletePostId(null)
  }

  const handleCreatePost = () => {
    setCreatePostModalOpen(false)
  }

  const handleClickCreatePost = () => {
    navigate(navigation.communities.post.new.fullPath())
  }

  return (
    <ResponsivePage.Body error={null} loading={isLoadingPostGroups} variant="full">
      {toDeletePostId && (
        <ConfirmDeleteModalOpen onClose={() => setToDeletePostId(null)} onSubmit={handleSubmit} />
      )}

      {createPostModalOpen && (
        <CreatePostModal
          onClose={() => setCreatePostModalOpen(false)}
          onSubmit={handleCreatePost}
        />
      )}

      <Box paddingX="s24" paddingTop="s24">
        <OneDataCollection
          source={source}
          visualizations={[
            {
              type: 'table',
              options: {
                columns: [
                  {
                    label: i18n.t('posts.communities.table.title'),
                    render: (post: PostsPost) => ({
                      type: 'text',
                      value: post.title,
                    }),
                  },
                  {
                    label: i18n.t('posts.communities.table.status'),
                    render: (post: PostsPost) => ({
                      type: 'dotTag',
                      value: {
                        label: post.scheduled
                          ? i18n.t('posts.communities.status.scheduled')
                          : post.publishedAt
                            ? i18n.t('posts.communities.status.published')
                            : i18n.t('posts.communities.status.draft'),
                        color: post.scheduled ? 'yellow' : post.publishedAt ? 'viridian' : 'grey',
                      },
                    }),
                  },
                  {
                    label: i18n.t('posts.communities.table.author'),
                    render: (post: PostsPost) => ({
                      type: 'person',
                      value: {
                        firstName: post.author?.fullName?.split(' ')[0] || '',
                        lastName: post.author?.fullName?.split(' ').slice(1).join(' ') || '',
                        src: buildUrl(post.author?.avatar) || undefined,
                      },
                    }),
                  },
                  {
                    label: i18n.t('posts.communities.table.posted_at'),
                    render: (post: PostsPost) => ({
                      type: 'text',
                      value: post.publishedAt || '-',
                    }),
                  },
                  {
                    label: i18n.t('posts.communities.table.last_edited_at'),
                    render: (post: PostsPost) => ({
                      type: 'text',
                      value: post.updatedAt,
                    }),
                  },
                  {
                    label: i18n.t('posts.communities.table.visibility'),
                    render: (post: PostsPost) => ({
                      type: 'tagList',
                      value: {
                        type: 'raw',
                        max: 2,
                        tags: [{ text: post.group?.title }],
                      },
                    }),
                  },
                ],
              },
            },
          ]}
        />
      </Box>
    </ResponsivePage.Body>
  )
}

export default PostsListPage
